import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Star, MessageSquare, Gamepad2, Calendar, GraduationCap, HeartHandshake, Home, FlaskConical } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { MonitoringLiveDemo } from '../../components/MonitoringLiveDemo';

const painel = [
  {
    icon: Star,
    title: 'Destaques',
    desc: 'Desenho mais reconhecido, rotina mais cheia e música mais tocada, num único painel.',
  },
  {
    icon: MessageSquare,
    title: 'Comunicação',
    desc: 'Subcategorias mais usadas, com contagem de toques por categoria.',
  },
  {
    icon: Gamepad2,
    title: 'Jogos',
    desc: 'Taxa de acerto, tempo médio de resposta e progresso geral.',
  },
  {
    icon: Calendar,
    title: 'Rotinas',
    desc: 'Frequência mensal em um gráfico simples, sempre os mesmos indicadores.',
  },
];

const publico = [
  { icon: GraduationCap, label: 'Professores' },
  { icon: HeartHandshake, label: 'Terapeutas' },
  { icon: Home, label: 'Famílias' },
  { icon: FlaskConical, label: 'Pesquisadores' },
];

const ProgressoPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.5] dark:opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(rgb(var(--warm)) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--warm)) 1px, transparent 1px)',
            backgroundSize: '42px 42px',
            maskImage: 'radial-gradient(ellipse 70% 60% at center, black 0%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at center, black 0%, transparent 75%)',
          }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-warm/10 dark:bg-warm/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <TrendingUp className="w-4 h-4 text-warm-ink dark:text-warm" />
            Acompanhamento de Progresso
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl leading-[1.1]">
            Você <span className="text-warm-ink dark:text-warm">acompanha</span> cada progresso
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl">
            Um painel único reúne o que a criança mais usa em cada módulo — em vez de
            perguntar "como foi hoje?", dá pra enxergar de verdade.
          </p>
        </div>
      </section>

      {/* Demo em largura total (o painel precisa de espaço pras duas colunas internas) + o que entra nele, abaixo */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto mb-14">
              <MonitoringLiveDemo />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-8">O que entra no painel</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {painel.map((item) => (
                <div key={item.title} className="rounded-2xl bg-card shadow-sm p-6">
                  <span className="w-10 h-10 rounded-lg bg-warm/10 dark:bg-warm/15 flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-warm-ink dark:text-warm" />
                  </span>
                  <h3 className="font-semibold text-foreground text-sm mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Pensado para quem acompanha de fora */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Pensado para quem acompanha de fora
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              O mesmo painel serve pra toda a rede de apoio da criança.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
              {publico.map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-muted-foreground">
                  <item.icon className="w-5 h-5" />
                  <span className="text-base font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-warm/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/comunicacao"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer a Comunicação Alternativa
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProgressoPage;
